import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, Ref, toRefs, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/useUserStore'
import { Game, Project, Player, DialogConfig, Group } from '@/types/main'
//import { consentStates } from '@/constants'
import { dateToFormattedString } from '@/utilities'
import SelectionBox from '../base/SelectionBox.vue'
import AnswerInput from '../base/AnswerInput.vue'
import Button from '@/components/base/Button.vue'
import { useGameStore } from '@/store/useGameStore'
import { useProjectStore } from '@/store/useProjectStore'
import { useAppStore } from '@/store/useAppStore'

/* interface LocationListItem {
  itemName: string
  item: string
} */
interface ProjectOptionListItem {
  itemName: string
  item: Project
}
interface PlayerOptionListItem {
  itemName: string
  item: Player
}

interface GroupShareOptionListItem {
  title: string
  id: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MonitorGameItem',
  props: {
  game: {
    type: Object as PropType<Game>,
    required: true,
  },
  edit: {
    type: Boolean,
  },
},
  emits: ['gamedeleted'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    listenAgain: 'Hør igjen',
    dialogDeleteTitle: 'Slett dette Spillet',
    dialogDeleteText: 'Hvis du sletter, du kan ikke angre. All spillerfremgang vil bli fjernet!',
    dialogDeleteConfirm: 'Slette',
    dialogDeleteCancel: 'Avbryt',
  },
  en: {
    listenAgain: 'Listen again',
    dialogDeleteTitle: 'Delete this Game',
    dialogDeleteText: 'Deletion cannot be undone. All player progress will be removed!',
    dialogDeleteConfirm: 'Delete',
    dialogDeleteCancel: 'Cancel',
  },
}




const { t } = useI18n({ messages })
const { actions: gameActions, getters: gameGetters } = useGameStore()
const { getters: userGetters } = useUserStore()
const { getters: projectGetters } = useProjectStore()
const { actions: appActions } = useAppStore()
const showSave = ref(false)
const g = new Game(props.game)
const theGame: Ref<Game> = ref(g)

/* const currentLocationName: Ref<LocationListItem> = ref({
  item: '',
  itemName: '',
}) */
const currentProjectName: Ref<ProjectOptionListItem> = ref({
  item: new Project(),
  itemName: '',
})
const currentPlayerName: Ref<PlayerOptionListItem> = ref({
  item: new Player(),
  itemName: '',
})

const initialise = () => {
  const { game } = toRefs(props)
  theGame.value = new Game(game.value)
  const myProject = projectGetters.projects.value.find((pr) => pr._id === theGame.value.project.id) || new Project()
  currentProjectName.value = {
    item: myProject,
    itemName: String(myProject.interventionName),
  }
}
initialise()

watch(
  () => props.game,
  () => {
    initialise()
  },
)

const gameGroups = computed(() => {
  const gGroups: Group[] = []
  theGame.value.sharing.groups.map((g) => {
    const group = userGetters.allGroups.value.find((ag) => ag._id === g)
    if (group) gGroups.push(group)
  })
  return gGroups
})

const ownerName = computed(() => {
  const myOwnerID = theGame.value.owner
  const u = userGetters.allUsers.value.find((u) => u._id === myOwnerID)
  return u ? u.profile.username : 'owner unknown'
})

const usersGroups = computed((): GroupShareOptionListItem[] => {
  return userGetters.selectedUser.value.groups.map((g) => {
    return {
      id: g._id,
      title: g.name,
    }
  })
})

// --------------- Project functions --------------------

const projectOptionList = computed((): ProjectOptionListItem[] => {
  return projectGetters.projects.value.map((pa) => ({
    item: pa,
    itemName: pa.interventionName,
  }))
})

const formattedDate = computed(() => {
  return theGame.value && theGame.value.project.lastSync ? dateToFormattedString(new Date(theGame.value.project.lastSync)) : 'No data'
})

const setProject = (value: ProjectOptionListItem) => {
  if (value.item) {
    if (theGame.value.project.id !== value.item._id) {
      theGame.value.project.shuffleTopLevel = false
      theGame.value.status.controlActive = false
      theGame.value.status.allowedSets = []
      theGame.value.project.topLevelOrder = []
    }
    theGame.value.project.id = value.item._id
    showSave.value = true
  }
}

// --------------- Player functions --------------------

const playerOptionList = computed((): PlayerOptionListItem[] => {
  return gameGetters.players.value
    .filter((pb) => {
      const matchingGroup = pb.groups.some((g) => theGame.value.sharing.groups.includes(g))
      const matchingOwner = pb.owner === theGame.value.owner //&&
      const notAlreadyAdded = !theGame.value.players.includes(pb._id)
      return (matchingGroup || matchingOwner) && notAlreadyAdded
    })
    .map((pa) => {
      return {
        item: pa,
        itemName: `${pa.profile.ref} - ${pa.profile.name}`,
      }
    })
})

const addPlayer = (value: PlayerOptionListItem) => {
  if (value.item) {
    theGame.value.players.push(value.item._id)
    showSave.value = true
  }
}

const removePlayer = (p: Player) => {
  if (p) {
    const i = theGame.value.players.indexOf(p._id)
    theGame.value.players.splice(i, 1)
    const j = theGame.value.activePlayers.indexOf(p._id)
    if (j > -1) theGame.value.activePlayers.splice(j, 1)
    showSave.value = true
  }
}

const addedPlayers = computed(() => {
  return gameGetters.players.value.filter((p) => theGame.value.players.includes(p._id))
})

// -------------------------Game functions-----------------------------

/* const locationList = computed((): LocationListItem[] => {
  return gameGetters.locations.map((i) => ({
    item: i,
    itemName: i,
  }))
})

const consentStateList = Object.values(consentStates).map((v, i) => ({
  id: i,
  title: v,
})) */

const saveGame = async () => {
  // Update a selected Game
  await gameActions.updateGame(theGame.value)
  showSave.value = false
}

const deleteGame = async () => {
  const dialogConfig: DialogConfig = {
    title: t('dialogDeleteTitle'),
    visible: true,
    text: t('dialogDeleteText'),
    cancel: () => appActions.setDialog(false),
    cancelText: t('dialogDeleteCancel'),
    confirm: async () => {
      appActions.setDialog(false)
      emit('gamedeleted')
      await gameActions.deleteGame(theGame.value)
    },
    confirmText: t('dialogDeleteConfirm'),
  }
  appActions.setDialog(true, dialogConfig)
  showSave.value = false
}

const saveActive = () => {
  showSave.value = true
}

const selectGame = () => {
  gameActions.selectGame(theGame.value)
}

const __returned__ = { messages, props, emit, t, gameActions, gameGetters, userGetters, projectGetters, appActions, showSave, g, theGame, currentProjectName, currentPlayerName, initialise, gameGroups, ownerName, usersGroups, projectOptionList, formattedDate, setProject, playerOptionList, addPlayer, removePlayer, addedPlayers, saveGame, deleteGame, saveActive, selectGame, ref, SelectionBox, AnswerInput, Button }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})